/**
 * Supplies Dual-Write Test Suite
 *
 * This utility automatically creates supplies with various configurations
 * and verifies that Supabase and Firestore are in sync.
 *
 * Usage:
 *   import { runSuppliesDualWriteTests } from './utils/suppliesDualWriteTest';
 *   await runSuppliesDualWriteTests(db, testConfig);
 */

import { sanitizeForFirestore } from '../global/Firebase/productSanitize'
import {
  saveNewSupply,
  deleteSupply,
  updateSupply,
} from '../global/Firebase/inventory';
import { api } from '../global/API/apiClient';
import { suppliesApi } from '../global/API/typedApiClient';
import { getDefaultStore } from 'jotai';
import {
  organizationAtom,
  storesDictAtom,
  productsDictAtom,
  dbAtom,
  userIdAtom,
  constantsAtom,
} from '../global/AtomsProvider';

// Test result tracking
const testResults = {
  passed: [],
  failed: [],
  skipped: [],
};

/**
 * Generate test supply data for a specific variation
 */
export function generateTestSupply(variation, testData) {
  const {
    organizationId,
    storeId,
    products,
    suppliers,
    paymentMethods,
  } = testData;

  // Base supply data
  const now = new Date();

  const baseSupply = {
    organizationId,
    store: storeId,
    supplyDate: now,
    dailyStatID: `test-${formatDateForDailyStat(now)}-${storeId}`,
    cashierUID: 'test-cashier',
    items: [],
    totalSum: 0,
    totalPrice: 0,
    isPaid: false,
    supplierID: null,
    supplierName: null,
    comment: '',
  };

  // Apply variation
  switch (variation.category) {
    case 'items':
      return applyItemsVariation(baseSupply, variation, testData);
    case 'payment':
      return applyPaymentVariation(baseSupply, variation, testData);
    case 'supplier':
      return applySupplierVariation(baseSupply, variation, testData);
    case 'optional':
      return applyOptionalFieldsVariation(baseSupply, variation, testData);
    case 'date':
      return applyDateVariation(baseSupply, variation, testData);
    case 'lifecycle':
      return applyLifecycleVariation(baseSupply, variation, testData);
    case 'cascading':
      return applyCascadingVariation(baseSupply, variation, testData);
    default:
      return baseSupply;
  }
}

function applyItemsVariation(supply, variation, testData) {
  const { products, storeId, paymentMethods } = testData;

  switch (variation.id) {
    case 'S1.1': // Single item
      supply.items = [createTestSupplyItem(products[0], 10, 0)];
      break;

    case 'S1.2': // Multiple items
      supply.items = products.slice(0, 3).map((p, i) =>
        createTestSupplyItem(p, (i + 1) * 5, 0)
      );
      break;

    case 'S1.3': // Item with return quantity
      supply.items = [createTestSupplyItem(products[0], 10, 2)];
      break;

    case 'S1.4': // Large quantity
      supply.items = [createTestSupplyItem(products[0], 1000, 0)];
      break;

    case 'S1.5': // Multiple items with returns
      supply.items = products.slice(0, 3).map((p, i) =>
        createTestSupplyItem(p, (i + 1) * 10, i)
      );
      break;
  }

  // Calculate totals
  supply.totalSum = supply.items.reduce(
    (sum, item) => sum + ((item.purchasePrice || 0) * (item.quantity - (item.returnQuantity || 0))),
    0
  );
  supply.totalPrice = supply.totalSum;

  return supply;
}

function applyPaymentVariation(supply, variation, testData) {
  const { products, storeId, paymentMethods } = testData;

  // Add default items
  supply.items = [createTestSupplyItem(products[0], 10, 0)];
  supply.totalSum = supply.items[0].purchasePrice * supply.items[0].quantity;
  supply.totalPrice = supply.totalSum;

  const cashMethod = paymentMethods.find(p => p.isCash);
  const cardMethod = paymentMethods.find(p => !p.isCash);

  switch (variation.id) {
    case 'S2.1': // Unpaid (default)
      supply.isPaid = false;
      supply.paymentMethod = null;
      break;

    case 'S2.2': // Paid with cash
      supply.isPaid = true;
      supply.paymentMethod = cashMethod || paymentMethods[0];
      supply.paymentDate = new Date();
      supply.paidBy = 'test-cashier';
      supply.createPaymentTransaction = true;
      break;

    case 'S2.3': // Paid with card
      supply.isPaid = true;
      supply.paymentMethod = cardMethod || paymentMethods[0];
      supply.paymentDate = new Date();
      supply.paidBy = 'test-cashier';
      supply.createPaymentTransaction = true;
      break;
  }

  return supply;
}

function applySupplierVariation(supply, variation, testData) {
  const { products, suppliers } = testData;

  // Add default items
  supply.items = [createTestSupplyItem(products[0], 10, 0)];
  supply.totalSum = supply.items[0].purchasePrice * supply.items[0].quantity;
  supply.totalPrice = supply.totalSum;

  switch (variation.id) {
    case 'S3.1': // No supplier
      supply.supplierID = null;
      supply.supplierName = null;
      break;

    case 'S3.2': // With supplier
      if (suppliers.length > 0) {
        supply.supplierID = suppliers[0].id;
        supply.supplierName = suppliers[0].name;
      } else {
        // Create a test supplier reference
        supply.supplierID = 'test-supplier-id';
        supply.supplierName = 'Test Supplier';
      }
      break;
  }

  return supply;
}

function applyOptionalFieldsVariation(supply, variation, testData) {
  const { products } = testData;

  // Add default items
  supply.items = [createTestSupplyItem(products[0], 10, 0)];
  supply.totalSum = supply.items[0].purchasePrice * supply.items[0].quantity;
  supply.totalPrice = supply.totalSum;

  switch (variation.id) {
    case 'S4.1': // With comment
      supply.comment = 'Test supply comment with special chars: !@#$%^&*()';
      break;

    case 'S4.2': // With supply order reference
      // Skip this test - requires a real supply order in Firestore
      // The saveNewSupply function tries to update the referenced supply order,
      // which fails if the document doesn't exist
      supply._lifecycleAction = 'skip';
      supply._lifecycleData = {
        skip: true,
        skipReason: 'Requires real supply order document in Firestore',
      };
      break;

    case 'S4.3': // With invoice image reference
      supply.invoiceImage = 'test-invoice-image.jpg';
      break;
  }

  return supply;
}

function applyDateVariation(supply, variation, testData) {
  const { products, storeId } = testData;

  // Add default items
  supply.items = [createTestSupplyItem(products[0], 10, 0)];
  supply.totalSum = supply.items[0].purchasePrice * supply.items[0].quantity;
  supply.totalPrice = supply.totalSum;

  switch (variation.id) {
    case 'S5.1': // Current date/time (default)
      supply.supplyDate = new Date();
      break;

    case 'S5.2': { // Yesterday
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(14, 30, 0, 0);
      supply.supplyDate = yesterday;
      supply.dailyStatID = `test-${formatDateForDailyStat(yesterday)}-${storeId}`;
      break;
    }

    case 'S5.3': { // 7 days ago
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      weekAgo.setHours(10, 0, 0, 0);
      supply.supplyDate = weekAgo;
      supply.dailyStatID = `test-${formatDateForDailyStat(weekAgo)}-${storeId}`;
      break;
    }

    case 'S5.4': { // 30 days ago
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      monthAgo.setHours(16, 45, 0, 0);
      supply.supplyDate = monthAgo;
      supply.dailyStatID = `test-${formatDateForDailyStat(monthAgo)}-${storeId}`;
      break;
    }
  }

  return supply;
}

function applyLifecycleVariation(supply, variation, testData) {
  const { products, paymentMethods, suppliers } = testData;

  // Start with a basic supply that can be modified
  supply.items = [createTestSupplyItem(products[0], 10, 0)];
  supply.totalSum = supply.items[0].purchasePrice * supply.items[0].quantity;
  supply.totalPrice = supply.totalSum;

  // Store the lifecycle action metadata on the supply for later use
  supply._lifecycleAction = variation.lifecycleAction;
  supply._lifecycleData = {};

  switch (variation.id) {
    case 'S6.1': // Mark as paid
      supply.isPaid = false;
      supply._lifecycleData = {
        markPaid: true,
        paymentInfo: {
          isPaid: true,
          paymentMethod: paymentMethods[0],
          paymentDate: new Date(),
          paidBy: 'test-cashier',
          createPaymentTransaction: true,
          paymentDailyStatID: supply.dailyStatID,
        },
      };
      break;

    case 'S6.2': // Mark as unpaid (reverse payment)
      supply.isPaid = true;
      supply.paymentMethod = paymentMethods[0];
      supply.paymentDate = new Date();
      supply.createPaymentTransaction = true;
      supply._lifecycleData = {
        markUnpaid: true,
        updateInfo: {
          isPaid: false,
          paymentMethod: null,
        },
      };
      break;

    case 'S6.3': // Delete supply
      supply._lifecycleData = {
        deleteSupply: true,
      };
      break;
  }

  return supply;
}

/**
 * Apply cascading verification variation
 * Each variation sets up data specifically to test a cascading write
 */
function applyCascadingVariation(supply, variation, testData) {
  const { products, paymentMethods, suppliers } = testData;

  // Default items for all cascading tests
  supply.items = products.slice(0, 2).map((p, i) =>
    createTestSupplyItem(p, (i + 1) * 5, 0)
  );
  supply.totalSum = supply.items.reduce(
    (sum, item) => sum + (item.purchasePrice * item.quantity),
    0
  );
  supply.totalPrice = supply.totalSum;

  // Store cascading focus metadata
  supply._cascadingFocus = variation.cascadingFocus;

  switch (variation.id) {
    case 'SUP_C1': // Verify movements created
      // Use items variation - movements should be created for each item
      break;

    case 'SUP_C2': // Verify stock incremented
      // Use items variation - stock should increment for each item
      break;

    case 'SUP_C3': // Paid supply - verify transaction
      supply.isPaid = true;
      supply.paymentMethod = paymentMethods[0];
      supply.paymentDate = new Date();
      supply.paidBy = 'test-cashier';
      supply.createPaymentTransaction = true;
      break;

    case 'SUP_C4': // Verify daily stats updated
      // Use items variation - daily stats should be updated
      break;

    case 'SUP_C5': // With supplier - verify supplier stats
      if (suppliers.length > 0) {
        supply.supplierID = suppliers[0].id;
        supply.supplierName = suppliers[0].name;
      } else {
        // Skip if no suppliers available
        supply._lifecycleData = {
          skip: true,
          skipReason: 'No suppliers available in test data',
        };
      }
      break;

    case 'SUP_C6': // Delete supply - verify cascading deletes
      // Will create supply then delete and verify all cascading writes are reversed
      supply._lifecycleAction = 'delete';
      supply._lifecycleData = {
        deleteSupply: true,
        verifyCascadingDeletes: true,
      };
      break;
  }

  return supply;
}

/**
 * Format date for dailyStatID (d-M-y format)
 */
function formatDateForDailyStat(date) {
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
}

/**
 * Execute lifecycle action after supply creation
 */
async function executeLifecycleAction(db, supplyRef, supplyData, testData) {
  const action = supplyData._lifecycleAction;
  const data = supplyData._lifecycleData;

  if (!action || !data) return { actionPerformed: null };

  // Handle skip case
  if (data.skip) {
    return { actionPerformed: 'skip', skipReason: data.skipReason };
  }

  // Wait a bit for initial sync
  await new Promise(resolve => setTimeout(resolve, 1000));

  try {
    if (data.markPaid) {
      // Mark as paid
      await updateSupply(db, supplyRef, data.paymentInfo, supplyData);
      return { actionPerformed: 'markPaid' };
    }

    if (data.markUnpaid) {
      // Mark as unpaid
      await updateSupply(db, supplyRef, data.updateInfo, supplyData);
      return { actionPerformed: 'markUnpaid' };
    }

    if (data.deleteSupply) {
      // Delete the supply
      await deleteSupply(db, supplyRef, supplyData);
      return { actionPerformed: 'delete' };
    }
  } catch (error) {
    console.error('Lifecycle action failed:', error.message);
    return { actionPerformed: action, error: error.message };
  }

  return { actionPerformed: null };
}

/**
 * Create a test supply item from a product
 */
function createTestSupplyItem(product, quantity, returnQuantity = 0) {
  const purchasePrice = product.purchasePrice || 500;

  return {
    id: product.id,
    name: product.name || 'Test Product',
    ref: product.ref,
    quantity,
    returnQuantity,
    purchasePrice,
    price: purchasePrice,
    totalPrice: purchasePrice * (quantity - returnQuantity),
    product: {
      name: product.name,
      purchasePrice,
    },
  };
}

/**
 * All test variations to run
 */
export const SUPPLY_TEST_VARIATIONS = [
  // Items variations
  { id: 'S1.1', category: 'items', name: 'Single item' },
  { id: 'S1.2', category: 'items', name: 'Multiple items' },
  { id: 'S1.3', category: 'items', name: 'Item with return quantity' },
  { id: 'S1.4', category: 'items', name: 'Large quantity (1000)' },
  { id: 'S1.5', category: 'items', name: 'Multiple items with returns' },

  // Payment variations
  { id: 'S2.1', category: 'payment', name: 'Unpaid supply' },
  { id: 'S2.2', category: 'payment', name: 'Paid with cash' },
  { id: 'S2.3', category: 'payment', name: 'Paid with card' },

  // Supplier variations
  { id: 'S3.1', category: 'supplier', name: 'No supplier' },
  { id: 'S3.2', category: 'supplier', name: 'With supplier' },

  // Optional fields
  { id: 'S4.1', category: 'optional', name: 'With comment' },
  { id: 'S4.2', category: 'optional', name: 'With supply order reference' },
  { id: 'S4.3', category: 'optional', name: 'With invoice image' },

  // Date variations
  { id: 'S5.1', category: 'date', name: 'Current date/time' },
  { id: 'S5.2', category: 'date', name: 'Yesterday' },
  { id: 'S5.3', category: 'date', name: '7 days ago' },
  { id: 'S5.4', category: 'date', name: '30 days ago' },

  // Lifecycle variations
  { id: 'S6.1', category: 'lifecycle', name: 'Mark as paid', lifecycleAction: 'markPaid' },
  { id: 'S6.2', category: 'lifecycle', name: 'Mark as unpaid', lifecycleAction: 'markUnpaid' },
  { id: 'S6.3', category: 'lifecycle', name: 'Delete supply', lifecycleAction: 'delete' },

  // Cascading write verifications (P0)
  { id: 'SUP_C1', category: 'cascading', name: 'Verify movements created', cascadingFocus: 'movements' },
  { id: 'SUP_C2', category: 'cascading', name: 'Verify stock incremented', cascadingFocus: 'stock' },
  { id: 'SUP_C3', category: 'cascading', name: 'Paid supply - verify transaction', cascadingFocus: 'transaction' },
  { id: 'SUP_C4', category: 'cascading', name: 'Verify daily stats updated', cascadingFocus: 'dailyStats' },
  { id: 'SUP_C5', category: 'cascading', name: 'With supplier - verify supplier stats', cascadingFocus: 'supplierStats' },
  { id: 'SUP_C6', category: 'cascading', name: 'Delete supply - verify cascading deletes', cascadingFocus: 'deleteReversals', lifecycleAction: 'delete' },
];

/**
 * Verify cascading writes for a supply
 * Returns detailed comparison of expected vs actual cascading data
 */
async function verifyCascadingWrites(firestoreId, supplyData, variation, testData) {
  const cascadingResults = {
    movements: null,
    stock: null,
    transaction: null,
    dailyStats: null,
    supplierStats: null,
  };

  const focus = variation.cascadingFocus;

  try {
    // Verify product movements (SUP_C1)
    if (focus === 'movements' || focus === 'deleteReversals') {
      cascadingResults.movements = await verifyProductMovements(firestoreId, supplyData, testData);
    }

    // Verify stock incremented (SUP_C2)
    if (focus === 'stock' || focus === 'deleteReversals') {
      cascadingResults.stock = await verifyStockIncremented(supplyData, testData);
    }

    // Verify transaction created (SUP_C3)
    if (focus === 'transaction' || focus === 'deleteReversals') {
      cascadingResults.transaction = await verifyTransactionCreated(firestoreId, supplyData, testData);
    }

    // Verify daily stats updated (SUP_C4)
    if (focus === 'dailyStats' || focus === 'deleteReversals') {
      cascadingResults.dailyStats = await verifyDailyStatsUpdated(supplyData, testData);
    }

    // Verify supplier stats (SUP_C5)
    if (focus === 'supplierStats' || focus === 'deleteReversals') {
      cascadingResults.supplierStats = await verifySupplierStatsUpdated(supplyData, testData);
    }
  } catch (error) {
    console.error('Error verifying cascading writes:', error.message);
    return {
      success: false,
      error: error.message,
      results: cascadingResults,
    };
  }

  // Check if all focused verifications passed
  const relevantResults = Object.entries(cascadingResults)
    .filter(([key, value]) => value !== null)
    .map(([key, value]) => ({ key, ...value }));

  const allPassed = relevantResults.every(r => r.matches);

  return {
    success: allPassed,
    results: cascadingResults,
    details: relevantResults,
  };
}

/**
 * Verify product movements were created for supply items
 * Movement type should be 'supply' with linked_doc_id pointing to supply
 */
async function verifyProductMovements(firestoreId, supplyData, testData) {
  const differences = [];
  const expectedItems = supplyData.items || [];

  try {
    // Fetch movements from Supabase via API
    const response = await api.get('/product-movements', {
      params: {
        linkedDocId: firestoreId,
        linkedDocType: 'supply',
        limit: 50,
      },
    });

    const movements = response.data || [];

    // Should have one movement per item
    if (movements.length !== expectedItems.length) {
      differences.push({
        field: 'movements.count',
        expected: expectedItems.length,
        actual: movements.length,
        severity: 'critical',
      });
    }

    // Verify each item has a corresponding movement
    for (const item of expectedItems) {
      const productId = item.id || item.product?.id;
      const expectedQuantity = item.quantity - (item.returnQuantity || 0);

      // Find movement for this product
      const movement = movements.find(m =>
        m.product_firestore_id === productId || m.product_id === productId
      );

      if (!movement) {
        differences.push({
          field: `movement.${productId}`,
          expected: 'exists',
          actual: 'not found',
          severity: 'critical',
        });
      } else {
        // Verify movement type
        if (movement.type !== 'supply') {
          differences.push({
            field: `movement.${productId}.type`,
            expected: 'supply',
            actual: movement.type,
            severity: 'critical',
          });
        }

        // Verify quantity (positive for supply)
        if (Math.abs(Number(movement.quantity) - expectedQuantity) > 0.01) {
          differences.push({
            field: `movement.${productId}.quantity`,
            expected: expectedQuantity,
            actual: movement.quantity,
            severity: 'critical',
          });
        }
      }
    }

    return {
      matches: differences.length === 0,
      differences,
      movementsFound: movements.length,
      expectedCount: expectedItems.length,
    };
  } catch (error) {
    return {
      matches: false,
      differences: [{ field: 'api_error', message: error.message }],
      error: error.message,
    };
  }
}

/**
 * Verify stock was incremented in product_store_stock
 * Note: This checks current stock state, so may need baseline comparison
 */
async function verifyStockIncremented(supplyData, testData) {
  const { storeId } = testData;
  const differences = [];
  const warnings = [];

  try {
    // For each item, verify stock exists in Supabase
    // Note: We can't verify exact increment without knowing pre-supply stock
    // This verification checks that stock records exist and are > 0
    for (const item of supplyData.items || []) {
      const productId = item.id || item.product?.id;
      const expectedIncrement = item.quantity - (item.returnQuantity || 0);

      try {
        const response = await api.get('/products/stock', {
          params: {
            productId,
            storeId,
          },
        });

        const stockData = response.data;

        if (!stockData || stockData.quantity === undefined) {
          differences.push({
            field: `stock.${productId}`,
            expected: 'exists',
            actual: 'not found',
            severity: 'warning', // Warning because stock might exist in Firestore only
          });
        } else {
          // Note: Cannot verify exact increment without baseline
          // Just verify stock record exists
          warnings.push(`Product ${productId}: stock = ${stockData.quantity} (increment was +${expectedIncrement})`);
        }
      } catch (error) {
        // Stock endpoint may not exist - log as warning
        warnings.push(`Could not verify stock for ${productId}: ${error.message}`);
      }
    }

    return {
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      warnings,
    };
  } catch (error) {
    return {
      matches: false,
      differences: [{ field: 'api_error', message: error.message }],
      error: error.message,
    };
  }
}

/**
 * Verify transaction was created for paid supply
 * Should be expense transaction linked to supply
 */
async function verifyTransactionCreated(firestoreId, supplyData, testData) {
  const differences = [];

  // Only check for paid supplies
  if (!supplyData.isPaid || !supplyData.createPaymentTransaction) {
    return {
      matches: true,
      differences: [],
      skipped: true,
      reason: 'Supply not paid or no transaction expected',
    };
  }

  try {
    const response = await api.get('/transactions', {
      params: {
        linkedDocId: firestoreId,
        linkedDocType: 'supply',
        limit: 10,
      },
    });

    const transactions = response.data || [];

    if (transactions.length === 0) {
      differences.push({
        field: 'transaction',
        expected: 'exists',
        actual: 'not found',
        severity: 'critical',
      });
    } else {
      const transaction = transactions[0];

      // Verify transaction type (expense for supply payment)
      if (transaction.type !== 'expense') {
        differences.push({
          field: 'transaction.type',
          expected: 'expense',
          actual: transaction.type,
          severity: 'critical',
        });
      }

      // Verify amount matches supply total
      const expectedAmount = supplyData.totalSum || supplyData.totalPrice;
      if (Math.abs(Number(transaction.value) - expectedAmount) > 0.01) {
        differences.push({
          field: 'transaction.value',
          expected: expectedAmount,
          actual: transaction.value,
          severity: 'critical',
        });
      }
    }

    return {
      matches: differences.length === 0,
      differences,
      transactionsFound: transactions.length,
    };
  } catch (error) {
    return {
      matches: false,
      differences: [{ field: 'api_error', message: error.message }],
      error: error.message,
    };
  }
}

/**
 * Verify daily stats were updated
 * Note: This is complex as daily_stats are aggregated
 */
async function verifyDailyStatsUpdated(supplyData, testData) {
  const { storeId } = testData;
  const differences = [];
  const warnings = [];

  try {
    const dailyStatId = supplyData.dailyStatID;

    if (!dailyStatId) {
      return {
        matches: false,
        differences: [{ field: 'dailyStatID', expected: 'exists', actual: 'missing' }],
      };
    }

    // Fetch daily stats from Supabase
    const response = await api.get('/reports/daily-stats', {
      params: {
        storeId,
        dailyStatId,
      },
    });

    const dailyStats = response.data;

    if (!dailyStats) {
      // Daily stats might not sync to Supabase yet - warning not error
      warnings.push('Daily stats not found in Supabase (may not be synced yet)');
      return {
        matches: true, // Not a failure - daily stats sync may be separate
        differences: [],
        warnings,
        notSynced: true,
      };
    }

    // Verify supplies amount is included
    // Note: Can't verify exact values without knowing baseline
    if (dailyStats.suppliesAmount !== undefined) {
      warnings.push(`Daily stats suppliesAmount: ${dailyStats.suppliesAmount}`);
    }

    return {
      matches: true,
      differences,
      warnings,
      dailyStatsFound: !!dailyStats,
    };
  } catch (error) {
    // Daily stats API may not exist
    return {
      matches: true, // Not a critical failure
      differences: [],
      warnings: [`Could not verify daily stats: ${error.message}`],
      apiError: error.message,
    };
  }
}

/**
 * Verify supplier stats were updated
 */
async function verifySupplierStatsUpdated(supplyData, testData) {
  const differences = [];

  // Only check for supplies with supplier
  if (!supplyData.supplierID) {
    return {
      matches: true,
      differences: [],
      skipped: true,
      reason: 'No supplier assigned',
    };
  }

  try {
    // Fetch supplier from Supabase
    const response = await api.get(`/suppliers/${supplyData.supplierID}`);
    const supplier = response.data;

    if (!supplier) {
      differences.push({
        field: 'supplier',
        expected: 'exists',
        actual: 'not found',
        severity: 'warning',
      });
    } else {
      // Verify lastSupplyDate is recent (if tracked)
      if (supplier.lastSupplyDate) {
        const lastSupplyDate = new Date(supplier.lastSupplyDate);
        const now = new Date();
        const diffHours = (now - lastSupplyDate) / (1000 * 60 * 60);

        if (diffHours > 24) {
          differences.push({
            field: 'supplier.lastSupplyDate',
            expected: 'recent',
            actual: `${diffHours.toFixed(1)} hours ago`,
            severity: 'warning',
          });
        }
      }
    }

    return {
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      supplierFound: !!supplier,
    };
  } catch (error) {
    return {
      matches: true, // Not a critical failure
      differences: [],
      warnings: [`Could not verify supplier stats: ${error.message}`],
      apiError: error.message,
    };
  }
}

/**
 * Verify cascading deletes after supply deletion
 */
async function verifyCascadingDeletes(firestoreId, supplyData, testData) {
  const results = {
    supplyDeleted: false,
    movementsDeleted: false,
    transactionDeleted: false,
  };
  const differences = [];

  try {
    // Check supply is deleted in Supabase
    const supplyResponse = await api.get('/supplies', {
      params: { firestoreId },
    });
    const supplies = supplyResponse.data || [];
    results.supplyDeleted = supplies.length === 0;

    if (!results.supplyDeleted) {
      differences.push({
        field: 'supply',
        expected: 'deleted',
        actual: 'still exists',
        severity: 'critical',
      });
    }

    // Check movements are deleted
    const movementsResponse = await api.get('/product-movements', {
      params: {
        linkedDocId: firestoreId,
        linkedDocType: 'supply',
      },
    });
    const movements = movementsResponse.data || [];
    results.movementsDeleted = movements.length === 0;

    if (!results.movementsDeleted) {
      differences.push({
        field: 'movements',
        expected: 'deleted',
        actual: `${movements.length} still exist`,
        severity: 'critical',
      });
    }

    // Check transaction is deleted (if supply was paid)
    if (supplyData.isPaid && supplyData.createPaymentTransaction) {
      const transResponse = await api.get('/transactions', {
        params: {
          linkedDocId: firestoreId,
          linkedDocType: 'supply',
        },
      });
      const transactions = transResponse.data || [];
      results.transactionDeleted = transactions.length === 0;

      if (!results.transactionDeleted) {
        differences.push({
          field: 'transaction',
          expected: 'deleted',
          actual: 'still exists',
          severity: 'critical',
        });
      }
    } else {
      results.transactionDeleted = true; // N/A
    }

    return {
      matches: differences.length === 0,
      differences,
      results,
    };
  } catch (error) {
    return {
      matches: false,
      differences: [{ field: 'api_error', message: error.message }],
      error: error.message,
      results,
    };
  }
}

/**
 * Run a single test variation
 */
async function runSingleTest(db, variation, testData, options = {}) {
  const { cleanup = true, delayMs = 2000 } = options;

  console.log(`\n--- Running test ${variation.id}: ${variation.name} ---`);

  try {
    // Generate test supply
    const supplyData = generateTestSupply(variation, testData);

    // Check if test should be skipped (can be set by any variation, not just lifecycle)
    if (supplyData._lifecycleData?.skip) {
      console.log(`SKIP: ${variation.name} - ${supplyData._lifecycleData.skipReason}`);
      testResults.skipped.push({ variation, reason: supplyData._lifecycleData.skipReason });
      return null;
    }

    // Create supply via frontend code (this triggers dual-write)
    // ATL-1503: saveNewSupply reports the payment state it saved (its leg id) — the lifecycle action's
    // precondition must be read from the supply AS SAVED, not from the create payload.
    const { ref: supplyRef, payment } = await saveNewSupply(db, supplyData);
    const firestoreId = supplyRef.id;

    console.log(`Created supply: ${firestoreId}`);

    // Wait for Supabase write to complete
    await new Promise(resolve => setTimeout(resolve, delayMs));

    // For lifecycle tests, perform the action after initial creation
    let lifecycleResult = null;
    if (variation.category === 'lifecycle' || (variation.category === 'cascading' && variation.lifecycleAction)) {
      console.log(`Performing lifecycle action: ${variation.lifecycleAction}`);
      lifecycleResult = await executeLifecycleAction(db, supplyRef, { ...supplyData, ...payment }, testData);
      console.log(`Lifecycle action result:`, lifecycleResult);

      // Handle skipped lifecycle tests
      if (lifecycleResult?.actionPerformed === 'skip') {
        console.log(`SKIP: ${variation.name} - ${lifecycleResult.skipReason}`);
        testResults.skipped.push({ variation, reason: lifecycleResult.skipReason });
        // Cleanup the created supply
        if (cleanup) {
          try {
            await deleteSupply(db, supplyRef, supplyData);
          } catch (e) { /* ignore */ }
        }
        return null;
      }

      // Wait for the update to sync
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }

    // Compare using the API
    let comparisonResult;
    let cascadingResult = null;

    // Special case: if supply was deleted, verify it's gone from both DBs
    if (lifecycleResult?.actionPerformed === 'delete') {
      comparisonResult = await verifyDeletion(firestoreId, supplyRef, db);

      // For cascading delete tests, also verify cascading deletes
      if (variation.category === 'cascading' && variation.cascadingFocus === 'deleteReversals') {
        console.log('Verifying cascading deletes...');
        cascadingResult = await verifyCascadingDeletes(firestoreId, supplyData, testData);
        console.log('Cascading delete verification:', cascadingResult);
      }
    } else {
      comparisonResult = await manualCompare(firestoreId, supplyRef, db, supplyData);

      // For cascading tests, verify the cascading writes
      if (variation.category === 'cascading') {
        console.log(`Verifying cascading writes (focus: ${variation.cascadingFocus})...`);
        cascadingResult = await verifyCascadingWrites(firestoreId, supplyData, variation, testData);
        console.log('Cascading verification result:', JSON.stringify(sanitizeForFirestore(cascadingResult), null, 2));
      }
    }

    // Determine overall pass/fail including cascading results
    const mainPassed = comparisonResult.matches;
    const cascadingPassed = cascadingResult ? cascadingResult.success || cascadingResult.matches : true;
    const overallPassed = mainPassed && cascadingPassed;

    // Log result
    if (overallPassed) {
      console.log(`PASS: ${variation.name}`);
      testResults.passed.push({
        variation,
        firestoreId,
        result: comparisonResult,
        cascadingResult,
        lifecycleResult,
      });
    } else {
      console.log(`FAIL: ${variation.name}`);
      if (!mainPassed) {
        console.log('Main differences:', JSON.stringify(sanitizeForFirestore(comparisonResult.differences), null, 2));
      }
      if (cascadingResult && !cascadingPassed) {
        console.log('Cascading differences:', JSON.stringify(sanitizeForFirestore(cascadingResult.differences || cascadingResult.details), null, 2));
      }
      testResults.failed.push({
        variation,
        firestoreId,
        result: comparisonResult,
        cascadingResult,
        lifecycleResult,
      });
    }

    // Cleanup - delete the test supply (skip if already deleted in lifecycle test)
    if (cleanup && lifecycleResult?.actionPerformed !== 'delete') {
      try {
        const supplyDoc = await supplyRef.get();
        if (supplyDoc.exists) {
          const firestoreData = supplyDoc.data();
          const savedSupplyData = {
            ...supplyData,
            ref: supplyRef,
            transactionId: firestoreData.transactionId,
          };
          await deleteSupply(db, supplyRef, savedSupplyData);
          console.log(`Cleaned up supply: ${firestoreId}`);
        }
      } catch (cleanupError) {
        console.warn(`Failed to cleanup supply ${firestoreId}:`, cleanupError.message);
      }
    }

    return comparisonResult;
  } catch (error) {
    console.error(`ERROR in test ${variation.id}:`, error.message);
    console.error(`Stack trace:`, error.stack);
    testResults.failed.push({
      variation,
      error: error.message,
      stack: error.stack,
    });
    return null;
  }
}

/**
 * Verify that a supply has been deleted from both databases
 * Uses retry logic to handle async deletion (15 retries × 2s = 30s max)
 */
async function verifyDeletion(firestoreId, supplyRef, db) {
  const maxRetries = 15;
  const retryDelayMs = 2000;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const differences = [];
    let firestoreExists = false;
    let supabaseExists = false;

    // Check Firestore
    try {
      const firestoreDoc = await supplyRef.get();
      firestoreExists = firestoreDoc.exists;
    } catch (error) {
      // Document not found is expected
      firestoreExists = false;
    }

    // Check Supabase
    try {
      const supplies = await suppliesApi.getSupplies({ firestoreId });
      supabaseExists = supplies && supplies.length > 0;
    } catch (error) {
      // Not found is expected
      supabaseExists = false;
    }

    // Both deleted - success!
    if (!firestoreExists && !supabaseExists) {
      console.log(`Delete verified on attempt ${attempt}: both databases show deleted`);
      return {
        firestoreId,
        foundInSupabase: false,
        foundInFirestore: false,
        matches: true,
        differences: [],
        warnings: [],
      };
    }

    // Build differences for logging
    if (firestoreExists) {
      differences.push({
        field: 'firestore_deletion',
        supabaseValue: 'deleted',
        firestoreValue: 'still exists',
        severity: 'critical',
      });
    }
    if (supabaseExists) {
      differences.push({
        field: 'supabase_deletion',
        supabaseValue: 'still exists',
        firestoreValue: 'deleted',
        severity: 'critical',
      });
    }

    // If last attempt, return failure
    if (attempt === maxRetries) {
      console.log(`Delete verification failed after ${maxRetries} attempts`);
      return {
        firestoreId,
        foundInSupabase: supabaseExists,
        foundInFirestore: firestoreExists,
        matches: false,
        differences,
        warnings: [`Deletion not complete after ${maxRetries} retries (${maxRetries * retryDelayMs / 1000}s)`],
      };
    }

    // Wait and retry
    console.log(`Delete verification attempt ${attempt}/${maxRetries}: waiting ${retryDelayMs}ms...`);
    await new Promise(resolve => setTimeout(resolve, retryDelayMs));
  }

  // Should not reach here
  return {
    firestoreId,
    foundInSupabase: true,
    foundInFirestore: true,
    matches: false,
    differences: [{ field: 'unexpected', supabaseValue: 'error', firestoreValue: 'error', severity: 'critical' }],
    warnings: ['Unexpected state in verifyDeletion'],
  };
}

/**
 * Manual comparison between Firestore and Supabase
 * Expanded field coverage matching sales best practices
 * Uses retry logic for Supabase fetch (first request may take longer due to warm-up)
 */
async function manualCompare(firestoreId, supplyRef, db, originalSupplyData) {
  const differences = [];
  const warnings = [];

  // Get Firestore data
  let firestoreData = null;
  try {
    const doc = await supplyRef.get();
    if (doc.exists) {
      firestoreData = { id: doc.id, ...doc.data() };
    }
  } catch (error) {
    warnings.push(`Could not fetch from Firestore: ${error.message}`);
  }

  // Get Supabase data with retry logic (first request may take longer)
  let supabaseData = null;
  const maxRetries = 3;
  const retryDelayMs = 2000;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Try to get supply by firestoreId
      const response = await api.get('/supplies', {
        params: { firestoreId },
      });
      const supplies = response.data || [];
      supabaseData = supplies.find(s => s.firestore_id === firestoreId);

      if (supabaseData) {
        if (attempt > 1) {
          console.log(`Found supply in Supabase on attempt ${attempt}`);
        }
        break;
      }

      // If not found and not last attempt, wait and retry
      if (attempt < maxRetries) {
        console.log(`Supply not found in Supabase, retrying in ${retryDelayMs}ms (attempt ${attempt}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, retryDelayMs));
      }
    } catch (error) {
      warnings.push(`Could not fetch from Supabase (attempt ${attempt}): ${error.message}`);
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelayMs));
      }
    }
  }

  if (!supabaseData) {
    differences.push({
      field: 'supply',
      supabaseValue: null,
      firestoreValue: 'exists',
      severity: 'critical',
    });
    return {
      firestoreId,
      foundInSupabase: false,
      foundInFirestore: !!firestoreData,
      matches: false,
      differences,
      warnings,
    };
  }

  // Expanded field comparison - matching sales best practices
  // Note: ID fields (supplier_id, paid_by) are resolved from Firestore IDs to UUIDs,
  // so we skip direct comparison and just verify they exist when expected
  const fieldsToCompare = [
    // Totals
    { supabase: 'total_sum', firestore: 'totalSum' },
    { supabase: 'total_price', firestore: 'totalPrice' },
    // Payment
    { supabase: 'is_paid', firestore: 'isPaid' },
    { supabase: 'create_payment_transaction', firestore: 'createPaymentTransaction' },
    // Supplier - compare existence, not exact value (UUID vs Firestore ID)
    { supabase: 'supplier_name', firestore: 'supplierName' },
    // Metadata
    { supabase: 'comment', firestore: 'comment' },
    // IDs and dates - cashier_uid is stored as-is (not resolved)
    { supabase: 'daily_stat_id', firestore: 'dailyStatID' },
    { supabase: 'cashier_uid', firestore: 'cashierUID' },
  ];

  // ID fields that are resolved (Firestore ID -> UUID) - only check existence
  const idFieldsToCheckExistence = [
    { supabase: 'supplier_id', firestore: 'supplierID', alt: 'supplierId' },
  ];

  for (const field of fieldsToCompare) {
    let supabaseValue = supabaseData[field.supabase];
    let firestoreValue = firestoreData?.[field.firestore];

    // Handle alternate firestore field names
    if (firestoreValue === undefined && field.alt) {
      firestoreValue = firestoreData?.[field.alt];
    }

    // Handle nested payment method (object -> id)
    if (field.supabase === 'payment_method' && typeof firestoreValue === 'object' && firestoreValue) {
      firestoreValue = firestoreValue.id || firestoreValue.name;
    }

    // Normalize values
    supabaseValue = normalizeValue(supabaseValue);
    firestoreValue = normalizeValue(firestoreValue);

    if (!valuesMatch(supabaseValue, firestoreValue)) {
      differences.push({
        field: field.supabase,
        supabaseValue,
        firestoreValue,
        severity: 'critical',
      });
    }
  }

  // Check ID fields for existence consistency (not exact value match)
  // These fields are resolved from Firestore IDs to UUIDs, so values will differ
  for (const field of idFieldsToCheckExistence) {
    let firestoreValue = firestoreData?.[field.firestore];
    if (firestoreValue === undefined && field.alt) {
      firestoreValue = firestoreData?.[field.alt];
    }
    const supabaseValue = supabaseData[field.supabase];

    const firestoreHasValue = firestoreValue !== null && firestoreValue !== undefined && firestoreValue !== '';
    const supabaseHasValue = supabaseValue !== null && supabaseValue !== undefined && supabaseValue !== '';

    if (firestoreHasValue !== supabaseHasValue) {
      differences.push({
        field: field.supabase,
        supabaseValue: supabaseHasValue ? 'has value' : 'null/empty',
        firestoreValue: firestoreHasValue ? 'has value' : 'null/empty',
        severity: 'warning',
      });
    }
  }

  // Compare payment_method separately (can be object or string)
  const supabasePM = supabaseData.payment_method;
  let firestorePM = firestoreData?.paymentMethod;
  if (typeof firestorePM === 'object' && firestorePM) {
    firestorePM = firestorePM.id || firestorePM.name;
  }
  if (!valuesMatch(normalizeValue(supabasePM), normalizeValue(firestorePM))) {
    differences.push({
      field: 'payment_method',
      supabaseValue: supabasePM,
      firestoreValue: firestorePM,
      severity: 'critical',
    });
  }

  // Compare items array in detail
  const supabaseItems = supabaseData.items || [];
  const firestoreItems = firestoreData?.items || [];

  if (supabaseItems.length !== firestoreItems.length) {
    differences.push({
      field: 'items.length',
      supabaseValue: supabaseItems.length,
      firestoreValue: firestoreItems.length,
      severity: 'critical',
    });
  } else {
    // Compare each item
    for (let i = 0; i < supabaseItems.length; i++) {
      const sItem = supabaseItems[i];
      const fItem = firestoreItems[i];

      // Check quantity
      if (Number(sItem.quantity) !== Number(fItem.quantity)) {
        differences.push({
          field: `items[${i}].quantity`,
          supabaseValue: sItem.quantity,
          firestoreValue: fItem.quantity,
          severity: 'critical',
        });
      }

      // Check return quantity
      const sReturnQty = Number(sItem.returnQuantity) || 0;
      const fReturnQty = Number(fItem.returnQuantity) || 0;
      if (sReturnQty !== fReturnQty) {
        differences.push({
          field: `items[${i}].returnQuantity`,
          supabaseValue: sReturnQty,
          firestoreValue: fReturnQty,
          severity: 'critical',
        });
      }

      // Check purchase price
      const sPurchasePrice = Number(sItem.purchasePrice) || 0;
      const fPurchasePrice = Number(fItem.purchasePrice) || Number(fItem.price) || 0;
      if (Math.abs(sPurchasePrice - fPurchasePrice) > 0.01) {
        differences.push({
          field: `items[${i}].purchasePrice`,
          supabaseValue: sPurchasePrice,
          firestoreValue: fPurchasePrice,
          severity: 'critical',
        });
      }
    }
  }

  return {
    firestoreId,
    foundInSupabase: true,
    foundInFirestore: !!firestoreData,
    matches: differences.length === 0,
    differences,
    warnings,
  };
}

function normalizeValue(value) {
  if (value === null || value === undefined) return null;
  if (value === '') return null;
  if (typeof value === 'number') return Number(value);
  if (typeof value === 'boolean') return value;
  return value;
}

function valuesMatch(a, b) {
  if ((a === null || a === undefined) && (b === null || b === undefined)) {
    return true;
  }
  // Treat null/undefined as equivalent to false for boolean fields
  // This handles cases like create_payment_transaction: null vs false
  if (typeof a === 'boolean' && (b === null || b === undefined)) {
    return a === false;
  }
  if (typeof b === 'boolean' && (a === null || a === undefined)) {
    return b === false;
  }
  if (a === null || a === undefined || b === null || b === undefined) {
    return false;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return Math.abs(a - b) < 0.01;
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b);
  }
  return String(a) === String(b);
}

/**
 * Run all test variations
 */
export async function runSuppliesDualWriteTests(passedDb, options = {}) {
  const {
    variations = SUPPLY_TEST_VARIATIONS,
    cleanup = true,
    delayMs = 5000, // Increased from 2000 to match sales best practices
    stopOnFailure = false,
    logSessionId = null,
  } = options;

  // Reset results
  testResults.passed = [];
  testResults.failed = [];
  testResults.skipped = [];

  // Get test data from Jotai store
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const productsDict = jotaiStore.get(productsDictAtom);
  const userId = jotaiStore.get(userIdAtom);

  // Get db from atom if not passed
  const db = passedDb || jotaiStore.get(dbAtom);

  if (!db) {
    throw new Error('Database not initialized. Please wait for app to fully load.');
  }

  if (!organization || Object.keys(organization).length === 0) {
    throw new Error('Organization not found. Please ensure you are logged in.');
  }

  if (!storesDict || Object.keys(storesDict).length === 0) {
    throw new Error('Stores not loaded. Please wait for app to fully load.');
  }

  // Get first store
  const storeId = Object.keys(storesDict)[0];
  if (!storeId) {
    throw new Error('No stores found. Please create a store first.');
  }

  const store = storesDict[storeId];

  // Get products (need at least 3 for multi-item tests)
  const products = Object.values(productsDict || {}).slice(0, 5);
  if (products.length === 0) {
    throw new Error('No products found. Please create products first.');
  }

  // Add refs to products if missing
  products.forEach(p => {
    if (!p.ref && p.id) {
      p.ref = db.collection('products').doc(p.id);
    }
  });

  // Fetch suppliers from Firestore
  let suppliers = [];
  try {
    const suppliersSnapshot = await db.collection('suppliers').limit(5).get();
    suppliers = suppliersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      ref: doc.ref,
    }));
    console.log(`Loaded ${suppliers.length} suppliers for testing`);
  } catch (error) {
    console.warn('Could not fetch suppliers:', error.message);
  }

  // Get payment methods from constants atom
  const constants = jotaiStore.get(constantsAtom);
  const allPaymentMethods = constants?.paymentMethods?.data || [];

  const paymentMethods = allPaymentMethods.filter(p =>
    p.destinationWalletForStore && Object.keys(p.destinationWalletForStore).length > 0
  );

  if (paymentMethods.length === 0) {
    // Create fallback payment methods
    const storeWallets = store?.wallets || organization.wallets || [];
    const defaultWallet = storeWallets[0] || { id: 'default-wallet', name: 'Default' };

    if (allPaymentMethods.length > 0) {
      allPaymentMethods.forEach(p => {
        paymentMethods.push({
          ...p,
          destinationWalletForStore: p.destinationWalletForStore || { [storeId]: defaultWallet },
        });
      });
    } else {
      paymentMethods.push({
        id: 'cash',
        name: 'Cash',
        isCash: true,
        destinationWalletForStore: { [storeId]: defaultWallet },
      });
      paymentMethods.push({
        id: 'card',
        name: 'Card',
        isCash: false,
        destinationWalletForStore: { [storeId]: defaultWallet },
      });
    }
  }

  const testData = {
    organizationId: organization.id,
    storeId,
    store,
    products,
    suppliers,
    paymentMethods,
    uid: userId || 'test-uid',
  };

  console.log('\n========================================');
  console.log('SUPPLIES DUAL-WRITE TEST SUITE');
  console.log('========================================');
  console.log(`Organization: ${organization.name || organization.id}`);
  console.log(`Store: ${store.name || storeId}`);
  console.log(`Store ID: ${storeId}`);
  console.log(`Products: ${products.length}`);
  console.log(`Suppliers: ${suppliers.length}`);
  console.log(`Payment methods: ${paymentMethods.length}`);
  console.log(`Variations to test: ${variations.length}`);
  console.log('========================================\n');

  // Run each test
  for (const variation of variations) {
    const result = await runSingleTest(db, variation, testData, { cleanup, delayMs });

    if (stopOnFailure && result && !result.matches) {
      console.log('\n Stopping due to failure (stopOnFailure=true)');
      break;
    }
  }

  // Print summary
  console.log('\n========================================');
  console.log('TEST SUMMARY');
  console.log('========================================');
  console.log(`Passed: ${testResults.passed.length}`);
  console.log(`Failed: ${testResults.failed.length}`);
  console.log(`Skipped: ${testResults.skipped.length}`);
  console.log('========================================\n');

  if (testResults.failed.length > 0) {
    console.log('FAILED TESTS:');
    testResults.failed.forEach(({ variation, result, error }) => {
      console.log(`  - ${variation.id}: ${variation.name}`);
      if (error) {
        console.log(`    Error: ${error}`);
      } else if (result?.differences) {
        result.differences.forEach(diff => {
          console.log(`    ${diff.field}: Supabase=${JSON.stringify(sanitizeForFirestore(diff.supabaseValue))} vs Firestore=${JSON.stringify(sanitizeForFirestore(diff.firestoreValue))}`);
        });
      }
    });
  }

  return testResults;
}

/**
 * Run a quick smoke test (just a few critical variations)
 */
export async function runQuickSuppliesSmokeTest(db = null) {
  const quickVariations = [
    SUPPLY_TEST_VARIATIONS.find(v => v.id === 'S1.1'), // Single item
    SUPPLY_TEST_VARIATIONS.find(v => v.id === 'S1.3'), // With return
    SUPPLY_TEST_VARIATIONS.find(v => v.id === 'S2.2'), // Paid
    SUPPLY_TEST_VARIATIONS.find(v => v.id === 'S3.2'), // With supplier
  ].filter(Boolean);

  return runSuppliesDualWriteTests(db, {
    variations: quickVariations,
    cleanup: true,
    delayMs: 5000, // Increased from 2000 - first request needs warm-up time
  });
}

/**
 * Run only cascading write verification tests (P0 priority)
 * These tests verify that all side effects (movements, stock, transactions, etc.)
 * are properly synced to Supabase
 */
export async function runCascadingSuppliesTests(db = null) {
  const cascadingVariations = SUPPLY_TEST_VARIATIONS.filter(v => v.category === 'cascading');

  console.log('\n========================================');
  console.log('SUPPLIES CASCADING WRITE TESTS (P0)');
  console.log('========================================');
  console.log('Testing:');
  cascadingVariations.forEach(v => console.log(`  - ${v.id}: ${v.name}`));
  console.log('========================================\n');

  return runSuppliesDualWriteTests(db, {
    variations: cascadingVariations,
    cleanup: true,
    delayMs: 5000,
  });
}

/**
 * Get cascading test variations only
 */
export const CASCADING_TEST_VARIATIONS = SUPPLY_TEST_VARIATIONS.filter(v => v.category === 'cascading');

export default {
  runSuppliesDualWriteTests,
  runQuickSuppliesSmokeTest,
  runCascadingSuppliesTests,
  generateTestSupply,
  SUPPLY_TEST_VARIATIONS,
  CASCADING_TEST_VARIATIONS,
};
